import type { Manhole, SewerPipeRun, LateralConnection, FoulBuildUpRowData } from '../types/tender.types';
import type { BuildUpRowDef } from './foulBuildUpRows';
import { backfillLabel, backfillEvalCode } from './backfillTypes';
import { findSewerRate } from './sewerRatesData';
import { findManholeRateVolume } from './manholeRatesVolumeData';
import { resolveBuildUpEvaluateCode, type PumpChamberConfigForEvaluateCode } from './foulBuildUpEvaluateCodesMapping';

// ── Types ─────────────────────────────────────────────────────────────────────
export type FoulSummaryRowType = 'section' | 'pipe-type' | 'item';

export interface FoulFormulaInfoRow { ref: string; value: number; unit: string }

export interface FoulFormulaInfo {
  label: string;
  formula: string;
  rows: FoulFormulaInfoRow[];
  total: number;
}

export interface FoulSummaryRow {
  key: string;
  rowType: FoulSummaryRowType;
  reference?: string;
  description?: string;
  quantity?: string;
  unit?: string;
  evaluateCode?: string;
  formulaInfo?: FoulFormulaInfo;
  children?: FoulSummaryRow[];
}

/**
 * Drop every leaf ('item') row whose quantity is blank or zero, then drop any
 * 'section'/'pipe-type' group whose children became empty as a result —
 * these builder functions push every row/group unconditionally regardless of
 * quantity, so a row with nothing entered (or computed to 0) otherwise shows
 * as a near-blank line in the Bill Summary and BOQ export. A row with no
 * `quantity` key at all (a group header, or an informational placeholder
 * like "No manholes entered yet") is never a candidate for removal here —
 * only rows the builder explicitly gave a quantity string to.
 *
 * `roundToWhole` must match how the CALLING builder's own tree is displayed:
 * `buildFoulSummaryData` feeds FoulSewersBillSummary.tsx, whose Qty column
 * shows `Math.round(n)` (a whole number) — a raw 0.3 there reads as "0" on
 * screen despite not being `=== 0`, so pass `true` (the default). But
 * `buildBuildUpSummaryData` feeds FoulBuildUpBillSummary.tsx (Pump Station /
 * Rising Main), whose Qty column shows the value to 2dp via `fmt()` — there a
 * genuine 0.30 reads as "0.30", not "0", so rounding before the zero-check
 * would wrongly hide a row the user can plainly see is non-zero; pass `false`.
 * The BOQ export always displays its own already-whole-number-rounded
 * `quantity` regardless of this flag — see `addLiveDomainSheet` in
 * ReviewCalculate.tsx, which prunes on that rounded value independently.
 */
function pruneZeroQuantityFoulRows(rows: FoulSummaryRow[], roundToWhole: boolean = true): FoulSummaryRow[] {
  return rows.reduce<FoulSummaryRow[]>((acc, row) => {
    if (row.children && row.children.length > 0) {
      const prunedChildren = pruneZeroQuantityFoulRows(row.children, roundToWhole);
      if (prunedChildren.length > 0) acc.push({ ...row, children: prunedChildren });
      return acc;
    }
    if (row.rowType === 'item' && row.quantity !== undefined) {
      const trimmed = row.quantity.trim();
      if (trimmed === '') return acc;
      const n = parseFloat(trimmed);
      if ((roundToWhole ? Math.round(n) : n) === 0) return acc;
    }
    acc.push(row);
    return acc;
  }, []);
}

// ── Lookup helpers ────────────────────────────────────────────────────────────
// Backfill label / eval-code live in the shared source of truth so a rename
// (e.g. "Type 1" → "Mot Type 1") only ever touches one file. Re-exported here so
// existing importers of this module keep working.
export { backfillLabel, backfillEvalCode };

export const pipeTypeEvalCode = (pt: string): string => {
  const m: Record<string, string> = { clay: 'PS', ultra: 'PU', concrete: 'PM', rigi: 'PR' };
  return m[pt?.toLowerCase()] ?? '';
};

// ── Quantity helpers ──────────────────────────────────────────────────────────
export const calcRockerQty = (
  pipeRuns: SewerPipeRun[],
  material: string,
  pipeDiameter: number,
): number =>
  pipeRuns
    .filter(
      (run) =>
        (run.pipeType ?? '').toLowerCase() === material.toLowerCase() &&
        parseInt(run.diameter ?? '', 10) === pipeDiameter,
    )
    .reduce((sum, run) => sum + (run.rockers ?? 0), 0);

export const calcConcreteProtection = (
  pipeRuns: SewerPipeRun[],
  pipeDiameter: number,
): number =>
  pipeRuns
    .filter((run) => parseInt(run.diameter ?? '', 10) === pipeDiameter)
    .reduce((sum, run) => sum + (run.concreteProtection ?? 0), 0);

export const calcLadderMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.ladders ?? 0), 0);

export const calcChainMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.safetyChains ?? 0), 0);

// ── Static template data ──────────────────────────────────────────────────────
export const FOUL_STUB_PIPE_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; dia: number }>;
}> = [
  {
    key: 'fstub-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100R',  desc: 'EO for 100mmØ Stub / rocker pipe', dia: 100 },
      { code: 'APS150R',  desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APS225R',  desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APS300R',  desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'fstub-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APU300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'fstub-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150SR', desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APRS225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APRS300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
      { code: 'APRS400SR', desc: 'EO for 400mmØ Stub / rocker pipe', dia: 400 },
      { code: 'APRS450SR', desc: 'EO for 450mmØ Stub / rocker pipe', dia: 450 },
      { code: 'APRS500SR', desc: 'EO for 500mmØ Stub / rocker pipe', dia: 500 },
      { code: 'APRS600SR', desc: 'EO for 600mmØ Stub / rocker pipe', dia: 600 },
      { code: 'APRS750SR', desc: 'EO for 750mmØ Stub / rocker pipe', dia: 750 },
    ],
  },
  {
    key: 'fstub-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
      { code: 'APM450SR', desc: 'EO for 450mmØ Stub / rocker pipe', dia: 450 },
      { code: 'APM525SR', desc: 'EO for 525mmØ Stub / rocker pipe', dia: 525 },
      { code: 'APM600SR', desc: 'EO for 600mmØ Stub / rocker pipe', dia: 600 },
      { code: 'APM675SR', desc: 'EO for 675mmØ Stub / rocker pipe', dia: 675 },
    ],
  },
];

export const FOUL_JUNCTION_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; mainDia: string; branchDia: string }>;
}> = [
  {
    key: 'fjunc-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100J1', desc: 'EO for 100 x 100mm junction', mainDia: '100mm', branchDia: '100mm' },
      { code: 'APS150J1', desc: 'EO for 150 x 100mm junction', mainDia: '150mm', branchDia: '100mm' },
      { code: 'APS150J2', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APS225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APS225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APS225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APS300J1', desc: 'EO for 300 x 100mm junction', mainDia: '300mm', branchDia: '100mm' },
      { code: 'APS300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APS300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APS300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'fjunc-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APU225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APU225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APU300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APU300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APU300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'fjunc-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150J1', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APRS225J1', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APRS300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APRS400J1', desc: 'EO for 400 x 150mm junction', mainDia: '400mm', branchDia: '150mm' },
      { code: 'APRS450J1', desc: 'EO for 450mm Saddle',          mainDia: '450mm', branchDia: '' },
      { code: 'APRS500J1', desc: 'EO for 500mm Saddle',          mainDia: '500mm', branchDia: '' },
      { code: 'APRS600J1', desc: 'EO for 600mm Saddle',          mainDia: '600mm', branchDia: '' },
      { code: 'APRS750J1', desc: 'EO for 750mm Saddle',          mainDia: '750mm', branchDia: '' },
    ],
  },
  {
    key: 'fjunc-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APM375J1', desc: 'EO for 375 x 150mm junction', mainDia: '375mm', branchDia: '150mm' },
      { code: 'APM450J1', desc: 'EO for 450 x 150mm junction', mainDia: '450mm', branchDia: '150mm' },
      { code: 'APMCD001', desc: 'Core Drill 525Ø+',             mainDia: '525mm', branchDia: '' },
    ],
  },
];

export const FOUL_EO_CONC_BED_ITEMS = [
  { code: 'AMHEO027', desc: 'EO concrete bed and surround 150mmØ pipe' },
  { code: 'AMHEO028', desc: 'EO concrete bed and surround 225mmØ pipe' },
  { code: 'AMHEO029', desc: 'EO concrete bed and surround 300mmØ pipe' },
  { code: 'AMHEO030', desc: 'EO concrete bed and surround 375mmØ pipe' },
  { code: 'AMHEO031', desc: 'EO concrete bed and surround 450mmØ pipe' },
  { code: 'AMHEO032', desc: 'EO concrete bed and surround 525mmØ pipe' },
  { code: 'AMHEO033', desc: 'EO concrete bed and surround 600mmØ pipe' },
  { code: 'AMHEO034', desc: 'EO concrete bed and surround 675mmØ pipe' },
];

export const FOUL_CONC_PROTECTION_ITEMS: Array<{ code: string; desc: string; dia: number }> = [
  { code: 'AMHEO008', desc: 'Concrete protection Slab 150mmØ', dia: 150 },
  { code: 'AMHEO009', desc: 'Concrete protection Slab 225mmØ', dia: 225 },
  { code: 'AMHEO010', desc: 'Concrete protection Slab 300mmØ', dia: 300 },
  { code: 'AMHEO012', desc: 'Concrete protection Slab 375mmØ', dia: 375 },
  { code: 'AMHEO013', desc: 'Concrete protection Slab 450mmØ', dia: 450 },
  { code: 'AMHEO014', desc: 'Concrete protection Slab 525mmØ', dia: 525 },
  { code: 'AMHEO015', desc: 'Concrete protection Slab 600mmØ', dia: 600 },
  { code: 'AMHEO016', desc: 'Concrete protection Slab 675mmØ', dia: 675 },
];

export const FOUL_EO_ITEMS = [
  { code: 'AMHEO006',  desc: 'EO for working in offsite carriageway',                         unit: 'm'  },
  { code: 'AMHEO007',  desc: 'EO trench reinstatement in carriageway',                        unit: 'm'  },
  { code: 'AMHEO003B', desc: 'EO construct manhole in existing carriageway',                  unit: 'Nr' },
  { code: 'AMHEO003',  desc: 'EO Breaking into existing manhole for connection',              unit: 'Nr' },
  { code: 'AMHEO003A', desc: 'EO Construct manhole on existing sewer',                       unit: 'Nr' },
  { code: 'ALP031',    desc: 'EO for impermeable geotextile membrane wrap to 150mmØ pipe',   unit: 'm'  },
];

export const FOUL_REDUNDANT_SEWER_ITEMS = [
  { desc: 'Grout / grub up redundant sewer - n ex 225mmØ', unit: 'm'  },
  { desc: 'Break out existing manhole, remove from site',   unit: 'Nr' },
];

// ── Main data builder ─────────────────────────────────────────────────────────

export function buildFoulSummaryData(
  pipeRuns: SewerPipeRun[],
  manholes: Manhole[],
  laterals: LateralConnection[],
  /** Optional per-key manually entered quantities (from eoConcBedQtys state or saved buildUp) */
  manualQtys?: Record<string, number>,
  manholeScheduleConfig?: { ladders: 'N/A' | 'Stainless' | 'Galvanised'; safetyChains: 'N/A' | 'Stainless' | 'Galvanised' },
): FoulSummaryRow[] {
  const ladderCfgLabel = manholeScheduleConfig?.ladders ?? 'N/A';
  const safetyCfgLabel = manholeScheduleConfig?.safetyChains ?? 'N/A';
  const getManual = (key: string): string => {
    const v = manualQtys?.[key];
    return v != null && v > 0 ? String(v) : '';
  };
  // Same as getManual, but permits negative values — for the road-construction
  // Credits rows below, which are entered as negative quantities.
  const getManualSigned = (key: string): string => {
    const v = manualQtys?.[key];
    return v != null && v !== 0 ? String(v) : '';
  };

  // ── Junction helpers ──────────────────────────────────────────────────────
  const getJunctionQty = (pipeType: string, mainDia: string, branchDia: string): number => {
    if (!branchDia) return 0;
    return pipeRuns
      .filter(
        (run, i) =>
          (run.pipeType ?? '').toLowerCase() === pipeType.toLowerCase() &&
          run.diameter === mainDia &&
          laterals[i]?.pipeDiameter === branchDia,
      )
      .reduce((sum, run) => sum + (run.junctions ?? 0), 0);
  };

  const getSaddleQty = (pipeType: string, mainDia: string): number =>
    pipeRuns
      .filter(
        (run) =>
          (run.pipeType ?? '').toLowerCase() === pipeType.toLowerCase() &&
          run.diameter === mainDia,
      )
      .reduce((sum, run) => sum + (run.junctions ?? 0), 0);

  const getCoreDrillQty = (): number =>
    pipeRuns
      .filter(
        (run) =>
          (run.pipeType ?? '').toLowerCase() === 'concrete' &&
          (parseInt((run.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525,
      )
      .reduce((sum, run) => sum + (run.junctions ?? 0), 0);

  // ── Drainage Runs section ─────────────────────────────────────────────────
  const drainageRunsRows: FoulSummaryRow[] = pipeRuns.map((run, i) => {
    const diamNum = (run.diameter || '').replace(/mm$/i, '');
    const bf = backfillLabel(run.backfillType ?? '');
    const dti = (run.runDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(run.pipeType ?? '');
    const evalCode = diamNum && run.backfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(run.backfillType)}\\${dti}`
      : '';
    const runLength = run.length || 0;
    const refLabel = `${run.fromMH}-${run.toMH}`;
    return {
      key: run.id || `run-${i}`,
      rowType: 'item',
      reference: run.ref || refLabel,
      description: `${refLabel} - ${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: runLength.toFixed(2),
      unit: 'm',
      evaluateCode: evalCode,
      formulaInfo: {
        label: `Run ${run.fromMH}–${run.toMH}`,
        formula: `Pipe run length · ${diamNum}mmØ ${run.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: `${run.fromMH}–${run.toMH}`, value: runLength, unit: 'm' }],
        total: runLength,
      },
    };
  });

  // ── Manhole rows ──────────────────────────────────────────────────────────
  const manholeItemRows: FoulSummaryRow[] = manholes.map((mh, i) => {
    const diamNum = (mh.diameter || '').replace(/mm$/i, '');
    const chamberType = mh.easiTrad === 'easi'
      ? ' Wide wall chamber'
      : mh.easiTrad === 'trad'
      ? 'Traditional chamber'
      : '';
    const dti = (mh.depthCategory || 0).toFixed(2);
    const chamberCode = mh.easiTrad === 'easi' ? 'Easi' : mh.easiTrad === 'trad' ? 'Trad' : '';
    const evalCode = chamberCode && diamNum ? `${chamberCode}\\${diamNum}\\${dti}` : '';
    return {
      key: mh.id || `mh-${i}`,
      rowType: 'item',
      reference: mh.ref,
      description: `${mh.ref} - ${diamNum}mmØ,${chamberType} dti - ${dti}m`,
      quantity: '1',
      unit: 'Nr',
      evaluateCode: evalCode,
      formulaInfo: {
        label: mh.ref,
        formula: `Manhole · ${diamNum}mmØ · ${mh.easiTrad ?? ''} chamber · depth to invert: ${dti}m`,
        rows: [{ ref: mh.ref, value: 1, unit: 'Nr' }],
        total: 1,
      },
    };
  });

  // ── Lateral rows ──────────────────────────────────────────────────────────
  const lateralItemRows: FoulSummaryRow[] = laterals.map((lat, i) => {
    const diamNum = (lat.pipeDiameter || '').replace(/mm$/i, '');
    // House Laterals have no backfill field of their own — the source sheet's
    // description formula reads the paired Main Sewer Run's Backfill column,
    // matched positionally by row (same convention as Gully Laterals).
    const runBackfillType = pipeRuns[i]?.backfillType;
    const bf = backfillLabel(runBackfillType ?? '');
    const dti = (lat.avgDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(lat.pipeType ?? '');
    const evalCode = diamNum && runBackfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(runBackfillType)}\\${dti}`
      : '';
    const latLength = lat.length || 0;
    const mhRef = lat.toManhole || '';
    return {
      key: lat.id || `lat-${i}`,
      rowType: 'item',
      reference: mhRef,
      description: `${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: latLength.toFixed(2),
      unit: 'm',
      evaluateCode: evalCode,
      formulaInfo: {
        label: mhRef,
        formula: `Lateral · ${diamNum}mmØ ${lat.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: mhRef, value: latLength, unit: 'm' }],
        total: latLength,
      },
    };
  });

  // ── Manhole accessories ───────────────────────────────────────────────────
  const manholeAccessories: FoulSummaryRow[] = [
    {
      key: 'mh-ladders', rowType: 'item',
      description: `Manhole ladders (if not incl in manhole price)${ladderCfgLabel !== 'N/A' ? ` - ${ladderCfgLabel}` : ''}`,
      quantity: calcLadderMetres(manholes).toFixed(2), unit: 'm',
      formulaInfo: {
        label: 'Manhole Ladders', formula: 'SUM(ladders) across all manholes',
        rows: manholes.filter((mh) => (mh.ladders ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.ladders ?? 0, unit: 'm' })),
        total: calcLadderMetres(manholes),
      },
    },
    {
      key: 'mh-chains', rowType: 'item',
      description: `Safety Chains${safetyCfgLabel !== 'N/A' ? ` - ${safetyCfgLabel}` : ''}`,
      quantity: calcChainMetres(manholes).toFixed(2), unit: 'Nr',
      formulaInfo: {
        label: 'Safety Chains', formula: 'SUM(safetyChains) across all manholes',
        rows: manholes.filter((mh) => (mh.safetyChains ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.safetyChains ?? 0, unit: 'Nr' })),
        total: calcChainMetres(manholes),
      },
    },
    {
      key: 'mh-amps', rowType: 'item',
      description: 'AMPS',
      quantity: String(manholes.reduce((sum, mh) => sum + (mh.amps ?? 0), 0)), unit: 'Nr',
      formulaInfo: {
        label: 'AMPS', formula: 'Sum amps count across all manholes',
        rows: manholes.filter((mh) => (mh.amps ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.amps ?? 0, unit: 'Nr' })),
        total: manholes.reduce((sum, mh) => sum + (mh.amps ?? 0), 0),
      },
    },
    {
      key: 'mh-backdrops', rowType: 'item',
      description: 'Backdrops',
      quantity: String(manholes.reduce((sum, mh) => sum + (mh.backdrop ?? 0), 0)), unit: 'Nr',
      formulaInfo: {
        label: 'Backdrops', formula: 'Sum backdrop count across all manholes',
        rows: manholes.filter((mh) => (mh.backdrop ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.backdrop ?? 0, unit: 'Nr' })),
        total: manholes.reduce((sum, mh) => sum + (mh.backdrop ?? 0), 0),
      },
    },
    {
      key: 'mh-muckstoppers', rowType: 'item',
      description: 'Muckstoppers',
      quantity: String(manholes.reduce((sum, mh) => sum + (mh.muckStopper ?? 0), 0)), unit: 'Nr',
      formulaInfo: {
        label: 'Muckstoppers', formula: 'Sum muckStopper count across all manholes',
        rows: manholes.filter((mh) => (mh.muckStopper ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.muckStopper ?? 0, unit: 'Nr' })),
        total: manholes.reduce((sum, mh) => sum + (mh.muckStopper ?? 0), 0),
      },
    },
    {
      key: 'mh-clegg', rowType: 'item',
      description: "Clegg testing to sewer b'fill (500mm layers at 30m intervals)",
      quantity: getManual('mh-clegg'), unit: 'Item',
    },
  ];

  // ── Manhole road adjustment ───────────────────────────────────────────────
  const manholeAdjRow: FoulSummaryRow = {
    key: 'mh-adjustment', rowType: 'pipe-type',
    reference: 'ADJUSTMENT FOR ROAD CONSTRUCTION GREATER THAN 450mm',
    children: [{
      key: 'mh-credits', rowType: 'pipe-type', reference: 'Credits',
      children: [
        { key: 'mh-adj-trench', rowType: 'item', description: 'Trench Excavation', quantity: getManualSigned('mh-adj-trench'), unit: 'm3' },
        { key: 'mh-adj-ews',    rowType: 'item', description: 'EWS',               quantity: getManualSigned('mh-adj-ews'),    unit: 'm2' },
        { key: 'mh-adj-disposal', rowType: 'item', description: 'Disposal',        quantity: getManualSigned('mh-adj-disposal'), unit: 'm3' },
        { key: 'mh-adj-backfill', rowType: 'item', description: 'Backfill',        quantity: getManualSigned('mh-adj-backfill'), unit: 'm3' },
      ],
    }],
  };

  const manholeSection: FoulSummaryRow = {
    key: 'manholes', rowType: 'section', description: 'Manholes',
    children: [
      ...(manholeItemRows.length > 0
        ? manholeItemRows
        : [{ key: 'manholes-empty', rowType: 'item' as const, description: 'No manholes entered yet' }]),
      ...manholeAccessories,
      manholeAdjRow,
    ],
  };

  const stubPipesSection: FoulSummaryRow = {
    key: 'stub-pipes', rowType: 'pipe-type', reference: 'Rocker / Stub Pipes',
    children: FOUL_STUB_PIPE_GROUPS.map((group) => ({
      key: group.key, rowType: 'pipe-type' as FoulSummaryRowType, reference: group.label,
      children: group.items.map((item) => {
        const qty = calcRockerQty(pipeRuns, group.pipeType, item.dia);
        return {
          key: `${group.key}-${item.code || item.desc}`,
          rowType: 'item' as FoulSummaryRowType,
          description: item.desc,
          quantity: String(qty),
          unit: 'Nr',
          evaluateCode: item.code || undefined,
          formulaInfo: {
            label: item.desc,
            formula: `Sum rockers from pipe runs where pipeType = '${group.pipeType}' AND diameter = ${item.dia}mm`,
            rows: pipeRuns
              .filter((r) => (r.pipeType ?? '').toLowerCase() === group.pipeType.toLowerCase() && parseInt(r.diameter ?? '', 10) === item.dia)
              .map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.rockers ?? 0, unit: 'Nr' })),
            total: qty,
          },
        };
      }),
    })),
  };

  const junctionsSection: FoulSummaryRow = {
    key: 'junctions', rowType: 'pipe-type', reference: 'Junctions',
    children: [
      ...FOUL_JUNCTION_GROUPS.map((group) => ({
        key: group.key, rowType: 'pipe-type' as FoulSummaryRowType, reference: group.label,
        children: group.items.map((item, idx) => {
          let qty = 0;
          let formulaInfo: FoulFormulaInfo | undefined;
          if (item.branchDia) {
            qty = getJunctionQty(group.pipeType, item.mainDia, item.branchDia);
            formulaInfo = {
              label: item.desc,
              formula: `Sum junctions where pipeType = '${group.pipeType}', pipe = ${item.mainDia}, lateral = ${item.branchDia}`,
              rows: pipeRuns
                .filter((r, i) =>
                  (r.pipeType ?? '').toLowerCase() === group.pipeType.toLowerCase() &&
                  r.diameter === item.mainDia &&
                  laterals[i]?.pipeDiameter === item.branchDia,
                )
                .map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.junctions ?? 0, unit: 'Nr' })),
              total: qty,
            };
          } else if (group.pipeType === 'rigi') {
            qty = getSaddleQty(group.pipeType, item.mainDia);
            formulaInfo = {
              label: item.desc,
              formula: `Sum junctions from Ridgisewer pipe runs where diameter = ${item.mainDia}`,
              rows: pipeRuns
                .filter((r) => (r.pipeType ?? '').toLowerCase() === 'rigi' && r.diameter === item.mainDia)
                .map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.junctions ?? 0, unit: 'Nr' })),
              total: qty,
            };
          } else if (group.pipeType === 'concrete') {
            qty = getCoreDrillQty();
            formulaInfo = {
              label: item.desc,
              formula: `Sum junctions from Concrete pipe runs where diameter >= 525mm`,
              rows: pipeRuns
                .filter((r) => (r.pipeType ?? '').toLowerCase() === 'concrete' && (parseInt((r.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525)
                .map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.junctions ?? 0, unit: 'Nr' })),
              total: qty,
            };
          }
          return {
            key: `${group.key}-${item.code || idx}`,
            rowType: 'item' as FoulSummaryRowType,
            description: item.desc,
            quantity: qty > 0 ? String(qty) : '',
            unit: 'Nr',
            evaluateCode: item.code || undefined,
            formulaInfo,
          };
        }),
      })),
      {
        key: 'fjunc-eo-conc-bed', rowType: 'pipe-type' as FoulSummaryRowType,
        reference: 'EO Concrete Bed & Surround',
        children: FOUL_EO_CONC_BED_ITEMS.map((item, idx) => ({
          key: `feo-conc-bed-${idx}`,
          rowType: 'item' as FoulSummaryRowType,
          description: item.desc,
          quantity: getManual(`feo-conc-bed-${idx}`),
          unit: 'm',
          evaluateCode: item.code,
        })),
      },
      {
        key: 'fjunc-conc-protection', rowType: 'pipe-type' as FoulSummaryRowType,
        reference: 'Concrete Protection Slab',
        children: FOUL_CONC_PROTECTION_ITEMS.map((item, idx) => {
          const qty = calcConcreteProtection(pipeRuns, item.dia);
          return {
            key: `fconc-protection-${idx}`,
            rowType: 'item' as FoulSummaryRowType,
            description: item.desc,
            quantity: String(qty),
            unit: 'm',
            evaluateCode: item.code,
            formulaInfo: {
              label: item.desc,
              formula: `Sum concreteProtection (m) from pipe runs where diameter = ${item.dia}mm`,
              rows: pipeRuns
                .filter((r) => parseInt(r.diameter ?? '', 10) === item.dia)
                .map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.concreteProtection ?? 0, unit: 'm' })),
              total: qty,
            },
          };
        }),
      },
    ],
  };

  const eoItemsSection: FoulSummaryRow = {
    key: 'eo-items', rowType: 'pipe-type', reference: 'EO Items',
    children: FOUL_EO_ITEMS.map((item, idx) => ({
      key: `feo-item-${idx}`,
      rowType: 'item' as FoulSummaryRowType,
      description: item.desc,
      quantity: getManual(`feo-item-${idx}`),
      unit: item.unit,
      evaluateCode: item.code || undefined,
    })),
  };

  const redundantSewerSection: FoulSummaryRow = {
    key: 'redundant-sewer', rowType: 'pipe-type', reference: 'Removal of Redundant Sewer',
    children: FOUL_REDUNDANT_SEWER_ITEMS.map((item, idx) => ({
      key: `fredundant-${idx}`,
      rowType: 'item' as FoulSummaryRowType,
      description: item.desc,
      quantity: getManual(`fredundant-${idx}`),
      unit: item.unit,
    })),
  };

  // NME / 1200 chamber lateral extras
  const nmeQty = laterals.reduce((sum, lat) => sum + (lat.nme ?? 0), 0);
  // The "1200" column holds each chamber's own depth (e.g. 3.25), not a Nr
  // count — so the chamber total is how many laterals have a depth entered,
  // and the depth breakdown below counts how many share each depth.
  const laterals1200 = laterals.filter((lat) => (lat.count1200 ?? 0) > 0);

  // 1200mmØ chamber depth breakdown — av 3.00m deep .. av 6.00m deep, in 0.25m steps.
  const DEPTH_BUCKETS_1200 = Array.from({ length: 13 }, (_, i) => (3 + i * 0.25).toFixed(2));
  const count1200DepthRows: FoulSummaryRow[] = DEPTH_BUCKETS_1200.map((d) => {
    const matching = laterals1200.filter((lat) => (lat.count1200 ?? 0).toFixed(2) === d);
    const qty = matching.length;
    return {
      key: `flat-1200-chamber-${d}`,
      rowType: 'item',
      description: `av ${d}m deep`,
      quantity: qty > 0 ? String(qty) : '',
      unit: 'Nr',
      formulaInfo: {
        label: `1200mmØ chamber av ${d}m deep`,
        formula: `COUNT(house laterals) where 1200 column = ${d}m`,
        rows: matching.map((lat) => ({ ref: lat.toManhole || '', value: 1, unit: 'Nr' })),
        total: qty,
      },
    };
  });

  const lateralSection: FoulSummaryRow = {
    key: 'lateral-connections', rowType: 'pipe-type', reference: 'Lateral Connections',
    children: [
      ...(lateralItemRows.length > 0
        ? lateralItemRows
        : [{ key: 'lateral-connections-empty', rowType: 'item' as FoulSummaryRowType, description: 'No lateral connections entered yet' }]),
      {
        key: 'flat-nme-chamber', rowType: 'item',
        description: 'NME Chamber',
        quantity: nmeQty > 0 ? String(nmeQty) : '',
        unit: 'Nr',
        formulaInfo: {
          label: 'NME Chamber', formula: 'SUM(nme) across all house laterals',
          rows: laterals.filter((lat) => (lat.nme ?? 0) > 0).map((lat) => ({ ref: lat.toManhole || '', value: lat.nme ?? 0, unit: 'Nr' })),
          total: nmeQty,
        },
      },
      {
        key: 'flat-1200-chamber', rowType: 'item',
        description: '1200mmØ chamber',
        quantity: '',
        unit: 'Nr',
      },
      ...count1200DepthRows,
    ],
  };

  // Only two top-level parent sections remain — Drainage Runs and Manholes.
  // Every other former top-level section (Rocker/Stub Pipes, Junctions, EO
  // Items, Removal of Redundant Sewer, Lateral Connections) now nests as a
  // pipe-type group under Drainage Runs.
  const drainageSection: FoulSummaryRow = {
    key: 'drainage-runs', rowType: 'section', description: 'Drainage Runs',
    children: [
      ...(drainageRunsRows.length > 0
        ? drainageRunsRows
        : [{ key: 'drainage-runs-empty', rowType: 'item' as FoulSummaryRowType, description: 'No pipe runs entered yet' }]),
      stubPipesSection,
      junctionsSection,
      eoItemsSection,
      redundantSewerSection,
      lateralSection,
    ],
  };

  return pruneZeroQuantityFoulRows([drainageSection, manholeSection]);
}

// ── Surplus / Backfill tables ────────────────────────────────────────────────
// Mirrors the 'Foul Summary' sheet's AE:AH (Surplus Runs/Lateral/Manholes/
// Total) and AJ:AM (Backfill Runs/Laterals/Manholes/Total) columns —
// docs/R&S-Takeoffsheet.xlsx.

export interface FoulSurplusBackfillRow {
  key: string;
  reference: string;
  description: string;
  runs: number;
  laterals: number;
  manholes: number;
  total: number;
  runsFormula: FoulFormulaInfo;
  lateralsFormula: FoulFormulaInfo;
  manholesFormula: FoulFormulaInfo;
}

export interface FoulSurplusBackfillData {
  surplus: FoulSurplusBackfillRow[];
  backfill: FoulSurplusBackfillRow[];
  surplusTotal: number;
  backfillTotal: number;
}

const parseDiameterMm = (d?: string): number => parseInt((d ?? '').replace(/mm$/i, ''), 10) || 0;

/**
 * One row per pipe run, pairing it with its house lateral (laterals[i], same
 * positional convention as buildFoulSummaryData's drainage/lateral rows) and
 * the manhole at the SAME row position in the Manhole Schedule (manholes[i])
 * — e.g. Foul Summary row 1 (pipeRuns[0]) uses Manhole Schedule row 1
 * (manholes[0]). This mirrors the source 'Foul Summary' sheet's own L column
 * (`=IF('Foul Manhole Schedule'!B9="","",'Foul Manhole Schedule'!B9)`),
 * which reads the Manhole Schedule by row position, NOT the Take Off sheet's
 * Manhole From/To columns — this is deliberately independent of `run.fromMH`
 * / `run.toMH`.
 *   Surplus Runs      = disposalArisings(run Ø, run dti)   × run length
 *   Surplus Lateral    = backfill(lateral Ø, lateral dti)  × lateral length  [see NOTE]
 *   Surplus Manholes   = disposalM3(manholes[i] Ø, dti)
 *   Total Surplus      = SUM of the three
 *   Backfill Runs      = backfill(run Ø, run dti)           × run length
 *   Backfill Laterals  = backfill(lateral Ø, lateral dti)   × lateral length
 *   Backfill Manholes  = backfillM3(manholes[i] Ø, dti)
 *   Total Backfill     = SUM of the three
 *
 * Rates come from SEWER_RATES (sewerRatesData.ts) keyed by pipe Ø + depth
 * category, and MANHOLE_RATES_VOLUME (manholeRatesVolumeData.ts) keyed by
 * manhole Ø + depth category — both extracted verbatim from the 'SEWER
 * RATES' / 'MANHOLE RATES' sheets. A run/lateral/manhole combination with no
 * matching rate row contributes 0, mirroring the source XLOOKUP(...,0)/
 * IFNA(...,0) fallback (also covers there being no manholes[i] at all, e.g.
 * more pipe runs than manhole schedule rows).
 *
 * NOTE — replicated from the source workbook, not a transcription slip: the
 * "Surplus Lateral" (AF) and "Backfill Laterals" (AK) columns use the exact
 * same underlying formula (both read SEWER RATES' "Backfill (m3/m)" column —
 * AF does NOT use "Disposal Arisings (m3/m)" the way Surplus Runs does).
 */
export function buildFoulSurplusBackfillData(
  pipeRuns: SewerPipeRun[],
  manholes: Manhole[],
  laterals: LateralConnection[],
): FoulSurplusBackfillData {
  const surplus: FoulSurplusBackfillRow[] = [];
  const backfill: FoulSurplusBackfillRow[] = [];

  pipeRuns.forEach((run, i) => {
    const lat = laterals[i];
    const fromManhole = manholes[i];

    const runDia = parseDiameterMm(run.diameter);
    const runDepth = run.runDepthCategory || 0;
    const runRate = findSewerRate(runDia, runDepth);
    const runLength = run.length || 0;

    const latDia = parseDiameterMm(lat?.pipeDiameter);
    const latDepth = lat?.avgDepthCategory || 0;
    const latRate = findSewerRate(latDia, latDepth);
    const latLength = lat?.length || 0;

    const mhDia = parseDiameterMm(fromManhole?.diameter);
    const mhDepth = fromManhole?.depthCategory;
    const mhRate = findManholeRateVolume(mhDia || undefined, mhDepth);

    const reference = run.ref || `${run.fromMH}-${run.toMH}`;
    const description = `${run.fromMH}-${run.toMH}`;

    const surplusRuns = runRate.disposalArisings * runLength;
    const surplusLateral = latRate.backfill * latLength; // see NOTE above
    const surplusManholes = mhRate.disposalM3;
    const totalSurplus = surplusRuns + surplusLateral + surplusManholes;

    const backfillRuns = runRate.backfill * runLength;
    const backfillLaterals = latRate.backfill * latLength;
    const backfillManholes = mhRate.backfillM3;
    const totalBackfill = backfillRuns + backfillLaterals + backfillManholes;

    surplus.push({
      key: run.id || `surplus-${i}`,
      reference,
      description,
      runs: surplusRuns,
      laterals: surplusLateral,
      manholes: surplusManholes,
      total: totalSurplus,
      runsFormula: {
        label: `Surplus Runs — ${description}`,
        formula: `Disposal Arisings(${runDia}mmØ, dti ${runDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Disposal Arisings rate', value: runRate.disposalArisings, unit: 'm3/m' },
          { ref: 'Length', value: runLength, unit: 'lm' },
        ],
        total: surplusRuns,
      },
      lateralsFormula: {
        label: `Surplus Lateral — ${description}`,
        formula: `Backfill(${latDia}mmØ, dti ${latDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: latRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: latLength, unit: 'lm' },
        ],
        total: surplusLateral,
      },
      manholesFormula: {
        label: `Surplus Manholes — ${description}`,
        formula: `Disposal(${fromManhole?.ref ?? '—'}, ${mhDia}mmØ, dti ${(mhDepth ?? 0).toFixed(2)}m)`,
        rows: [
          { ref: 'Disposal rate', value: mhRate.disposalM3, unit: 'm3' },
        ],
        total: surplusManholes,
      },
    });

    backfill.push({
      key: run.id || `backfill-${i}`,
      reference,
      description,
      runs: backfillRuns,
      laterals: backfillLaterals,
      manholes: backfillManholes,
      total: totalBackfill,
      runsFormula: {
        label: `Backfill Runs — ${description}`,
        formula: `Backfill(${runDia}mmØ, dti ${runDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: runRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: runLength, unit: 'lm' },
        ],
        total: backfillRuns,
      },
      lateralsFormula: {
        label: `Backfill Laterals — ${description}`,
        formula: `Backfill(${latDia}mmØ, dti ${latDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: latRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: latLength, unit: 'lm' },
        ],
        total: backfillLaterals,
      },
      manholesFormula: {
        label: `Backfill Manholes — ${description}`,
        formula: `Granular Backfill(${fromManhole?.ref ?? '—'}, ${mhDia}mmØ, dti ${(mhDepth ?? 0).toFixed(2)}m)`,
        rows: [
          { ref: 'Backfill rate', value: mhRate.backfillM3, unit: 'm3' },
        ],
        total: backfillManholes,
      },
    });
  });

  return {
    surplus,
    backfill,
    surplusTotal: surplus.reduce((s, r) => s + r.total, 0),
    backfillTotal: backfill.reduce((s, r) => s + r.total, 0),
  };
}

// ── Build Up (Pump Station / Rising Main) summary builder ──────────────────────
// Turns a flat BuildUpRowDef[] template plus saved quant/evaluateCode entries into
// a FoulSummaryRow tree: rows marked isHeader become pipe-type group headers that
// collect the items following them, separators are dropped, ungrouped leading rows
// stay at the root.
export function buildBuildUpSummaryData(
  rows: BuildUpRowDef[],
  buildUp?: Record<string, FoulBuildUpRowData>,
  /** Drives mesh-reinforcement/Wet Well rows' evaluate code — see `resolveBuildUpEvaluateCode`.
   * Pump Station-specific; omit for Rising Main (its rows never hit those branches). */
  pumpChamberConfig?: PumpChamberConfigForEvaluateCode,
  /** When set, leading rows that sit above the first header are wrapped in a
   * 'pipe-type' section with this key/label instead of staying loose at the root
   * (Pump Station's site-works rows). Bill Summary / export only. */
  ungroupedSection?: { key: string; label: string },
): FoulSummaryRow[] {
  const data = buildUp ?? {};
  const result: FoulSummaryRow[] = [];
  let currentGroup: FoulSummaryRow | null = null;
  if (ungroupedSection) {
    currentGroup = { key: ungroupedSection.key, rowType: 'pipe-type', reference: ungroupedSection.label, children: [] };
    result.push(currentGroup);
  }

  rows.forEach((row) => {
    if (row.isSeparator) return;

    if (row.isHeader) {
      currentGroup = { key: row.id, rowType: 'pipe-type', reference: row.description, children: [] };
      result.push(currentGroup);
      return;
    }

    const entry = data[row.id];
    const quant = entry?.quant ?? row.defaultQuant;
    const item: FoulSummaryRow = {
      key: row.id,
      rowType: 'item',
      description: row.description,
      quantity: quant != null ? String(quant) : '',
      unit: row.unit,
      evaluateCode: resolveBuildUpEvaluateCode(row, pumpChamberConfig) || undefined,
    };

    if (currentGroup) {
      currentGroup.children!.push(item);
    } else {
      result.push(item);
    }
  });

  return pruneZeroQuantityFoulRows(result, false);
}
